/* eslint-disable no-magic-numbers */
import { RGBFormat } from './color';
import { SeededRandom } from '../seeded-random';

/**
 * Represents a color in HSL color space
 */
interface HSLColor {
  hue: number; // 0-360
  saturation: number; // 0-1
  lightness: number; // 0-1
}

/**
 * Generates colors that fit in the Tailwind 300-600 range (not too bright or dark)
 * with distinct hues to ensure visual differentiation. When hues are similar,
 * ensures saturation and lightness are significantly different.
 */
export class ColorGenerator {
  private readonly usedColors: HSLColor[] = [];
  private readonly minHueDifference = 30; // Minimum degrees apart on color wheel
  private readonly random: SeededRandom | null;

  /**
   * Creates a new ColorGenerator
   * @param seed Optional seed for deterministic color generation. Same seed produces same colors.
   */
  constructor(seed?: number) {
    this.random = seed !== undefined ? new SeededRandom(seed) : null;
  }

  /**
   * Generates a color in the Tailwind 300-600 range with RGB values roughly between 0.3-0.7
   * @param avoidHues Optional array of hues (0-360) to avoid for better differentiation
   * @returns Color object with RGB values in range suitable for visibility
   */
  public generateColor(avoidHues: number[] = []): RGBFormat {
    const hslColor = this.generateDistinctColor(avoidHues);
    this.usedColors.push(hslColor);

    return this.hslToRgb(hslColor.hue, hslColor.saturation, hslColor.lightness);
  }

  /**
   * Generates an array of distinct colors for the given number of items
   * @param count Number of colors to generate
   * @returns Array of Color objects
   */
  public generateColors(count: number): RGBFormat[] {
    this.usedColors.length = 0; // Reset used colors
    const colors: RGBFormat[] = [];

    for (let i = 0; i < count; i++) {
      colors.push(this.generateColor());
    }

    return colors;
  }

  /**
   * Gets the next random number (0-1), using seeded random if available
   * @returns Random number between 0 and 1
   */
  private nextRandom(): number {
    return this.random ? this.random.random() : Math.random();
  }

  /**
   * Generates a color that is maximally distinct from all previously used colors.
   * Uses a strategy that tries to find the best color by:
   * 1. First trying to find a hue that's far from all existing hues
   * 2. If hues must be similar, ensures saturation/lightness are very different
   * @param avoidHues Array of hues to avoid
   * @returns HSL color that is as distinct as possible from existing colors
   */
  private generateDistinctColor(avoidHues: number[]): HSLColor {
    const maxAttempts = 100;
    let bestColor: HSLColor | null = null;
    let bestScore = -Infinity;

    // First pass: try to find random colors with good scores
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const candidate: HSLColor = {
        hue: this.nextRandom() * 360,
        // Tailwind 300-600 range roughly corresponds to:
        // - Saturation: 60-80% (vibrant but not oversaturated)
        // - Lightness: 45-65% (not too dark, not too bright)
        saturation: 0.6 + this.nextRandom() * 0.2,
        lightness: 0.45 + this.nextRandom() * 0.2,
      };

      const score = this.calculateColorDistinctness(candidate, avoidHues);

      if (score > bestScore) {
        bestScore = score;
        bestColor = candidate;
      }

      // If we found a perfect candidate (far from all colors), use it immediately
      if (score >= 1.0) {
        break;
      }
    }

    // If we didn't find a good random color, use strategic hue selection
    if (bestColor === null || bestScore < 0.5) {
      return this.generateColorWithBestHue(avoidHues);
    }

    return bestColor;
  }

  /**
   * Strategically generates a color by finding the hue that's furthest from all existing hues,
   * then varying saturation/lightness to ensure distinction from colors with similar hues.
   * @param avoidHues Additional hues to avoid
   * @returns HSL color with optimal hue and varied sat/light
   */
  private generateColorWithBestHue(avoidHues: number[]): HSLColor {
    // Find the hue that's furthest from all existing hues
    const bestHue = this.findBestAvailableHue(avoidHues);

    // Check if any existing colors use a similar hue
    const similarHueColors = this.usedColors.filter((color) => {
      const hueDiff = Math.abs(color.hue - bestHue);
      const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff);
      return normalizedHueDiff < this.minHueDifference;
    });

    // If no similar hue exists, use standard sat/light values
    if (similarHueColors.length === 0) {
      return {
        hue: bestHue,
        saturation: 0.7,
        lightness: 0.55,
      };
    }

    // Similar hue exists - vary saturation/lightness significantly
    // Try to find sat/light values that are maximally different from similar hue colors
    let bestSatLight = { saturation: 0.7, lightness: 0.55 };
    let maxMinDistance = -Infinity;

    // Try different sat/light combinations
    const satLightCandidates = [
      { saturation: 0.6, lightness: 0.45 },
      { saturation: 0.6, lightness: 0.65 },
      { saturation: 0.8, lightness: 0.45 },
      { saturation: 0.8, lightness: 0.65 },
      { saturation: 0.7, lightness: 0.5 },
      { saturation: 0.7, lightness: 0.6 },
      { saturation: 0.65, lightness: 0.55 },
      { saturation: 0.75, lightness: 0.55 },
    ];

    for (const { saturation, lightness } of satLightCandidates) {
      // Find minimum distance to similar hue colors
      let minDist = Infinity;
      for (const similarColor of similarHueColors) {
        const satDiff = Math.abs(saturation - similarColor.saturation);
        const lightDiff = Math.abs(lightness - similarColor.lightness);
        const dist = Math.max(satDiff, lightDiff);
        minDist = Math.min(minDist, dist);
      }

      if (minDist > maxMinDistance) {
        maxMinDistance = minDist;
        bestSatLight = { saturation, lightness };
      }
    }

    return {
      hue: bestHue,
      ...bestSatLight,
    };
  }

  /**
   * Finds the hue that's furthest from all existing hues.
   * Samples the hue space to find the optimal hue.
   * @param avoidHues Additional hues to avoid
   * @returns The hue value (0-360) that's maximally distant from existing hues
   */
  private findBestAvailableHue(avoidHues: number[]): number {
    const allUsedHues = [...this.usedColors.map((c) => c.hue), ...avoidHues];

    if (allUsedHues.length === 0) {
      // No colors yet, return a random hue
      return this.nextRandom() * 360;
    }

    // Sample the hue space to find the hue with maximum minimum distance
    const sampleCount = 36; // Sample every 10 degrees
    let bestHue = 0;
    let maxMinDistance = -1;

    for (let i = 0; i < sampleCount; i++) {
      const candidateHue = (360 / sampleCount) * i;

      // Find minimum distance to all used hues
      let minDistance = Infinity;
      for (const usedHue of allUsedHues) {
        const hueDiff = Math.abs(candidateHue - usedHue);
        const normalizedDiff = Math.min(hueDiff, 360 - hueDiff);
        minDistance = Math.min(minDistance, normalizedDiff);
      }

      if (minDistance > maxMinDistance) {
        maxMinDistance = minDistance;
        bestHue = candidateHue;
      }
    }

    // Add some randomness to the best hue (within a small range)
    const randomOffset = (this.nextRandom() - 0.5) * 10; // ±5 degrees
    bestHue = (bestHue + randomOffset + 360) % 360;

    return bestHue;
  }

  /**
   * Calculates how distinct a candidate color is from all existing colors.
   * Returns a score between 0 (very similar) and 1+ (very distinct).
   * @param candidate The candidate color to evaluate
   * @param avoidHues Additional hues to avoid
   * @returns A distinctness score
   */
  private calculateColorDistinctness(
    candidate: HSLColor,
    avoidHues: number[]
  ): number {
    if (this.usedColors.length === 0 && avoidHues.length === 0) {
      return 1.0; // First color is always distinct
    }

    let minDistance = Infinity;

    // Check against all used colors
    for (const usedColor of this.usedColors) {
      const distance = this.calculateColorDistance(candidate, usedColor);
      minDistance = Math.min(minDistance, distance);
    }

    // Check against avoid hues (treat as colors with average sat/light)
    for (const avoidHue of avoidHues) {
      const avoidColor: HSLColor = {
        hue: avoidHue,
        saturation: 0.7,
        lightness: 0.55,
      };
      const distance = this.calculateColorDistance(candidate, avoidColor);
      minDistance = Math.min(minDistance, distance);
    }

    // Normalize the distance to a 0-1+ score
    // A distance of 0.5 or more is considered very distinct
    return Math.min(minDistance * 2, 1.5);
  }

  /**
   * Calculates the perceptual distance between two colors in HSL space.
   * Takes into account:
   * - Hue difference (with wrap-around)
   * - When hues are similar, heavily weights saturation/lightness differences
   * @param color1 First color
   * @param color2 Second color
   * @returns Distance value (0 = identical, 1 = maximally different)
   */
  private calculateColorDistance(color1: HSLColor, color2: HSLColor): number {
    // Calculate hue difference with wrap-around
    const hueDiff = Math.abs(color1.hue - color2.hue);
    const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff) / 180; // 0-1

    // Calculate saturation and lightness differences
    const satDiff = Math.abs(color1.saturation - color2.saturation);
    const lightDiff = Math.abs(color1.lightness - color2.lightness);

    // If hues are very different, that's the main factor
    if (normalizedHueDiff > this.minHueDifference / 180) {
      // Hues are sufficiently different - weight hue heavily
      return normalizedHueDiff * 0.7 + (satDiff + lightDiff) * 0.15;
    } else {
      // Hues are similar - weight saturation/lightness differences more heavily
      // to ensure visual distinction
      const slDiff = Math.max(satDiff, lightDiff);
      return normalizedHueDiff * 0.3 + slDiff * 0.7;
    }
  }

  /**
   * Converts HSL color values to RGB Color object
   * @param h Hue (0-360)
   * @param s Saturation (0-1)
   * @param l Lightness (0-1)
   * @returns Color object with RGB values (0-1)
   */
  private hslToRgb(h: number, s: number, l: number): RGBFormat {
    const hNormalized = h / 360;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((hNormalized * 6) % 2) - 1));
    const m = l - c / 2;

    let r = 0;
    let g = 0;
    let b = 0;

    const hSector = hNormalized * 6;

    if (hSector < 1) {
      r = c;
      g = x;
    } else if (hSector < 2) {
      r = x;
      g = c;
    } else if (hSector < 3) {
      g = c;
      b = x;
    } else if (hSector < 4) {
      g = x;
      b = c;
    } else if (hSector < 5) {
      r = x;
      b = c;
    } else {
      r = c;
      b = x;
    }

    return { r: r + m, g: g + m, b: b + m };
  }

  /**
   * Resets the generator state, clearing used colors
   */
  public reset(): void {
    this.usedColors.length = 0;
  }
}
